import type { TutorialStep } from '../components/Tutorial';

export function buildTutorialSteps(triggerId?: string, conditionId?: string, actionId?: string, waitId?: string): TutorialStep[] {
  return [
    {
      title: '👋 Hey — let\'s make a workflow.',
      body: 'Workflows are simple: when something happens, do something. Like "when a customer texts, reply with our hours." That\'s it. This whole page builds those for you. 60 seconds and you\'ve got it.',
      placement: 'center',
    },
    {
      target: '[data-tour="toolbar"]',
      title: '👆 The toolbar up top',
      body: 'Name your workflow here, then hit Save to keep your work. Test run pretends to fire it with fake data so you can see what would happen. The green Active switch turns it on for real.',
      placement: 'bottom',
      padding: 6,
    },
    {
      target: '[data-tour="active-toggle"]',
      title: '🟢 Active vs Paused',
      body: 'Green = live and firing on real events. Gray = paused (saved but not firing). Always test with Paused first, flip to Active when you\'re happy. You can pause anytime.',
      placement: 'left',
    },
    {
      target: '[data-tour="palette"]',
      title: '🧱 Building blocks · left side',
      body: 'Drag any block onto the workspace in the middle. "When this happens" is always first. Then add Wait, Only if, or Then do this. The ⭐ Ask AI block lets you call an AI model inside the workflow.',
      placement: 'right',
      padding: 8,
    },
    {
      target: '[data-tour="templates"]',
      title: '⭐ Ready-made workflows',
      body: 'Don\'t want to start from scratch? Click any of these templates and they\'ll drop straight onto the canvas. Tweak from there. The fastest way to get going.',
      placement: 'right',
      padding: 8,
    },
    {
      target: triggerId ? `[data-node-id="${triggerId}"]` : '.vnode.trig',
      title: '⚡ When this happens',
      body: 'Every workflow starts with a "when." This one fires when a hail storm hits a customer\'s zip code. You can also start workflows from missed calls, texts, new leads, invoices going unpaid — anything Impact IQ already tracks.',
      placement: 'right',
      zoomToNodeId: triggerId,
      padding: 10,
    },
    {
      target: conditionId ? `[data-node-id="${conditionId}"]` : '.vnode.cond',
      title: '🔀 Only if (the filter)',
      body: 'Filters narrow things down. "Only send to customers who haven\'t been serviced in 12 months." The workflow splits two ways: ✓ match goes one direction, ✕ no match goes another. Drag from either bottom dot to wire up both branches.',
      placement: 'right',
      zoomToNodeId: conditionId,
      padding: 10,
    },
    {
      target: actionId ? `[data-node-id="${actionId}"]` : '.vnode.act',
      title: '📨 Then do this',
      body: 'This is the action that actually happens — send an SMS, send an email, create a task, hit a webhook. The {{curly tags}} in the message body get replaced with real customer info when it fires.',
      placement: 'right',
      zoomToNodeId: actionId,
      padding: 10,
    },
    {
      target: waitId ? `[data-node-id="${waitId}"]` : '.vnode.wait',
      title: '⏱ Wait a bit',
      body: 'Sometimes you don\'t want to fire instantly. Wait 30 seconds before texting back. Wait 2 days before asking for a review. Wait 3 days before a payment reminder. Pick any duration.',
      placement: 'right',
      zoomToNodeId: waitId,
      padding: 10,
    },
    {
      target: '[data-tour="canvas"]',
      title: '🔗 Connecting blocks',
      body: 'Drag from the dot at the bottom of one block to the dot at the top of another to wire them together. Gold lines show the flow. Click a line to highlight it, press Delete to remove it.',
      placement: 'center',
      emphasizeCanvas: true,
    },
    {
      target: '[data-tour="props"]',
      title: '✏️ Editing blocks',
      body: 'Click any block on the canvas and this panel opens up. Change the message, swap the trigger, tweak the wait time — everything is editable, no popups. Press Delete to remove the selected block.',
      placement: 'left',
      padding: 8,
    },
    {
      target: '[data-tour="test-run"]',
      title: '▶ Test run · safe simulation',
      body: 'Click Test run and the workflow pretends to fire against fake customer data. The Test Run drawer in the bottom-right shows every step, every condition match, every action — color-coded. Verify it works before flipping Active.',
      placement: 'bottom',
    },
    {
      target: '.assistant-fab',
      title: '✨ Or just ask the AI',
      body: 'Don\'t want to drag blocks around? Click "Build with AI" in the bottom-left, describe your workflow in plain English ("when a hail cell hits a customer zip, send a free inspection link"), and it builds the whole thing instantly. Pick which AI model to use from the dropdown.',
      placement: 'right',
      padding: 12,
    },
    {
      target: '[data-tour="save"]',
      title: '💾 Save your work',
      body: 'Cmd/Ctrl + S anywhere on the page works. Workflows save automatically to this browser. Use the ⋯ menu to Export as JSON for backup, or Import a workflow file someone else shared.',
      placement: 'bottom',
    },
    {
      title: '🦅 You\'re ready!',
      body: 'Try dropping a block. Edit something. Wire it up. Hit Test run. Flip Active when you\'re happy. You can replay this tutorial anytime via the ? button in the top-right. Now go build something.',
      placement: 'center',
    },
  ];
}
